import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Loader2, SendHorizonal, BookmarkPlus, X, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", phone: "", message: "" };
const inputCls = "mt-1 w-full bg-[#0C0C0C] border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F2B705]/50";

function localDatetimeMin() {
  const d = new Date(Date.now() + 60000);
  d.setSeconds(0, 0);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

/** Admin can message anybody — a booking/reservation customer or someone brand new — by
 * typing their email and/or phone directly. Not limited to replying to an inbound message.
 * Also supports Quick Templates (save/reuse common text) and Scheduled Send (future send_at). */
export default function ComposeMessage({ token, onSent, onScheduled }) {
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState("");
  const [templates, setTemplates] = useState([]);
  const [scheduling, setScheduling] = useState(false);
  const [sendAt, setSendAt] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/message-templates`, H).then(({ data }) => setTemplates(data)).catch(() => {});
    // eslint-disable-next-line
  }, [open]);

  const useTemplate = (t) => { set("message", t.body); toast.success(`"${t.title}" inserted.`); };

  const saveTemplate = async () => {
    if (!form.message.trim()) return toast.error("Type a message first, then save it as a template.");
    const title = window.prompt("Name this template (e.g. \"Late return reminder\"):");
    if (!title || !title.trim()) return;
    try {
      const { data } = await axios.post(`${API}/message-templates`, { title: title.trim(), body: form.message }, H);
      setTemplates((prev) => [...prev, data].sort((a, b) => a.title.localeCompare(b.title)));
      toast.success("Template saved.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save template."); }
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete the "${t.title}" template?`)) return;
    try {
      await axios.delete(`${API}/message-templates/${t.id}`, H);
      setTemplates((prev) => prev.filter((x) => x.id !== t.id));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete template."); }
  };

  const validateBase = (method) => {
    if (!form.message.trim()) { toast.error("Type a message first."); return false; }
    if (!form.email.trim() && !form.phone.trim()) { toast.error("Enter an email address or phone number."); return false; }
    if ((method === "sms" || method === "both") && !form.phone.trim()) { toast.error("Enter a phone number to send a text."); return false; }
    if ((method === "email" || method === "both") && !form.email.trim()) { toast.error("Enter an email address to send an email."); return false; }
    return true;
  };

  const send = async (method) => {
    if (!validateBase(method)) return;
    setSending(method);
    try {
      const { data } = await axios.post(`${API}/contact/compose`,
        { name: form.name, email: form.email, phone: form.phone, message: form.message, method }, H);
      const ok = Object.entries(data.results).filter(([, v]) => v).map(([k]) => k.toUpperCase()).join(" + ");
      toast.success(`Message sent via ${ok}.`);
      onSent?.(data.contact);
      setForm(empty); setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the message.");
    } finally {
      setSending("");
    }
  };

  const schedule = async (method) => {
    if (!validateBase(method)) return;
    if (!sendAt) return toast.error("Pick a date and time to send this.");
    setSending(method);
    try {
      await axios.post(`${API}/contact/schedule`,
        { name: form.name, email: form.email, phone: form.phone, message: form.message, method,
          send_at: new Date(sendAt).toISOString() }, H);
      toast.success(`Scheduled for ${new Date(sendAt).toLocaleString()}.`);
      setForm(empty); setSendAt(""); setScheduling(false); setOpen(false);
      onScheduled?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not schedule the message.");
    } finally {
      setSending("");
    }
  };

  return (
    <>
      <button data-testid="compose-message-btn" onClick={() => setOpen(true)}
        className="flex items-center gap-2 text-sm bg-[#F2B705] text-black font-bold px-4 py-2 rounded-md hover:bg-white transition-colors">
        <Plus size={16} /> New Message
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="compose-message-dialog" className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-[520px] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">New Message</DialogTitle>
            <DialogDescription className="text-white/45">
              Text or email anyone — a current customer or someone new — by entering their email and/or phone number.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-white/45">Name <span className="text-white/30 normal-case">(optional)</span></label>
              <input data-testid="compose-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-white/45">Email</label>
                <input data-testid="compose-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="optional if phone given" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/45">Phone</label>
                <input data-testid="compose-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="optional if email given" />
              </div>
            </div>

            {templates.length > 0 && (
              <div data-testid="compose-templates-list" className="flex flex-wrap gap-1.5">
                {templates.map((t) => (
                  <span key={t.id} data-testid={`compose-template-${t.id}`}
                    className="group flex items-center gap-1 bg-white/5 border border-white/10 rounded-full pl-3 pr-1 py-1 text-xs text-white/70">
                    <button type="button" onClick={() => useTemplate(t)} className="hover:text-[#F2B705]">{t.title}</button>
                    <button type="button" data-testid={`compose-template-delete-${t.id}`} onClick={() => deleteTemplate(t)}
                      className="opacity-40 hover:opacity-100 hover:text-red-400 rounded-full p-0.5"><X size={10} /></button>
                  </span>
                ))}
              </div>
            )}

            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs uppercase tracking-widest text-white/45">Message</label>
                <button type="button" data-testid="compose-save-template-btn" onClick={saveTemplate}
                  className="flex items-center gap-1 text-[11px] text-white/45 hover:text-[#F2B705]">
                  <BookmarkPlus size={12} /> Save as template
                </button>
              </div>
              <textarea data-testid="compose-message-input" rows={4} className={inputCls} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="What do you want to say?" />
            </div>

            <label className="flex items-center gap-2 text-xs text-white/60 cursor-pointer">
              <input data-testid="compose-schedule-toggle" type="checkbox" checked={scheduling}
                onChange={(e) => { setScheduling(e.target.checked); if (e.target.checked && !sendAt) setSendAt(localDatetimeMin()); }}
                className="accent-[#F2B705]" />
              <Clock size={12} /> Schedule for later instead of sending now
            </label>
            {scheduling && (
              <input data-testid="compose-schedule-datetime" type="datetime-local" min={localDatetimeMin()} value={sendAt}
                onChange={(e) => setSendAt(e.target.value)} className={inputCls} />
            )}

            <div className="flex flex-wrap gap-2 pt-1">
              {!scheduling ? (
                <>
                  <button data-testid="compose-send-sms" onClick={() => send("sms")} disabled={!!sending}
                    className="flex items-center gap-1.5 text-sm font-bold bg-[#F2B705] text-black px-4 py-2 rounded-md hover:bg-white disabled:opacity-50">
                    {sending === "sms" ? <Loader2 size={14} className="animate-spin" /> : <SendHorizonal size={14} />} Send by Text
                  </button>
                  <button data-testid="compose-send-email" onClick={() => send("email")} disabled={!!sending}
                    className="flex items-center gap-1.5 text-sm font-bold border border-white/20 text-white/75 px-4 py-2 rounded-md hover:border-[#F2B705] hover:text-[#F2B705] disabled:opacity-50">
                    {sending === "email" ? <Loader2 size={14} className="animate-spin" /> : <SendHorizonal size={14} />} Send by Email
                  </button>
                  <button data-testid="compose-send-both" onClick={() => send("both")} disabled={!!sending}
                    className="flex items-center gap-1.5 text-sm font-bold border border-white/20 text-white/55 px-4 py-2 rounded-md hover:border-[#F2B705] hover:text-[#F2B705] disabled:opacity-50">
                    {sending === "both" ? <Loader2 size={14} className="animate-spin" /> : <SendHorizonal size={14} />} Send Both
                  </button>
                </>
              ) : (
                <>
                  <button data-testid="compose-schedule-sms" onClick={() => schedule("sms")} disabled={!!sending}
                    className="flex items-center gap-1.5 text-sm font-bold bg-[#F2B705] text-black px-4 py-2 rounded-md hover:bg-white disabled:opacity-50">
                    {sending === "sms" ? <Loader2 size={14} className="animate-spin" /> : <Clock size={14} />} Schedule Text
                  </button>
                  <button data-testid="compose-schedule-email" onClick={() => schedule("email")} disabled={!!sending}
                    className="flex items-center gap-1.5 text-sm font-bold border border-white/20 text-white/75 px-4 py-2 rounded-md hover:border-[#F2B705] hover:text-[#F2B705] disabled:opacity-50">
                    {sending === "email" ? <Loader2 size={14} className="animate-spin" /> : <Clock size={14} />} Schedule Email
                  </button>
                  <button data-testid="compose-schedule-both" onClick={() => schedule("both")} disabled={!!sending}
                    className="flex items-center gap-1.5 text-sm font-bold border border-white/20 text-white/55 px-4 py-2 rounded-md hover:border-[#F2B705] hover:text-[#F2B705] disabled:opacity-50">
                    {sending === "both" ? <Loader2 size={14} className="animate-spin" /> : <Clock size={14} />} Schedule Both
                  </button>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
